import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { enums } from '@kattana-io/common';
import Text from '../../atoms/Text';
import Button from '../../molecules/Button';
import FeatureTable, { FeatureTableRow } from './FeatureTable';
import FaqTable, { FaqTableRow } from './FaqTable';
import { DEFAULT_URL_PATH } from '../../helpers/constants';
import { actionShowModal } from '../../actions/modal';
import { ModalTypes } from '../../reducers/modal';
import { authTokenSelector } from '../../selectors/auth';
import { actionCreateNotification } from '../../actions/notifications';
import { UserAPI } from '../../store/createStore';

import kattana from './icons/kattana.svg';
import kattanaPremium from './icons/kattana_premium.svg';

const features: FeatureTableRow[] = [
  { type: 'text_with_question_mark', value: 'Price alerts', tooltip: '1' },
  {
    type: 'text_with_question_mark',
    value: 'Token unlocks on charts',
    tooltip: '1',
  },
  {
    type: 'text_with_question_mark',
    value: 'Minimal TF on charts',
    tooltip: '1',
  },
  {
    type: 'text_with_question_mark',
    value: 'Download trade history',
    tooltip: '1',
  },
  {
    type: 'text_with_question_mark',
    value: 'Uniswap V3 liquidity chart',
    tooltip: '1',
  },
  {
    type: 'text_with_question_mark',
    value: 'Top wallets in Magic Wallet Analyzer',
    tooltip: '1',
  },
  { type: 'text_with_question_mark', value: 'Top traders', tooltip: '1' },
  {
    type: 'text_with_question_mark',
    value: 'Samurai Club (soon)',
    tooltip: '1',
  },
  { type: 'text_with_question_mark', value: 'Whale swap (soon)', tooltip: '1' },
  {
    type: 'text_with_question_mark',
    value: 'Telegram notifications (soon)',
    tooltip: '1',
  },
];

const commons: FeatureTableRow[] = [
  { type: 'text', value: 5 },
  { type: 'mark', value: false },
  { type: 'text', value: '1 min' },
  { type: 'text', value: '1 day or last 100 trades' },
  { type: 'mark', value: false },
  { type: 'text', value: 'Top 10' },
  { type: 'text', value: '1 day' },
  { type: 'mark', value: false },
  { type: 'mark', value: false },
  { type: 'mark', value: false },
];

const premiums: FeatureTableRow[] = [
  { type: 'text', value: 'Unlimited' },
  { type: 'mark', value: true },
  { type: 'text', value: '1 sec' },
  { type: 'text', value: 'For all time' },
  { type: 'mark', value: true },
  { type: 'text', value: 'All' },
  { type: 'text', value: 'For all time' },
  { type: 'text_with_mark', mark: true, value: '(via application form)' },
  { type: 'mark', value: true },
  { type: 'mark', value: true },
];

const faqs: FaqTableRow[] = [
  {
    question:
      'How long is the Premium subscription valid in case of payment with USDT?',
    answear: '30 days from the payment time',
  },
  {
    question:
      'I have 400 KTN tokens or more in my connected wallet - do I need to do anything else to activate the premium?',
    answear:
      'No, All premium features are available to you as long as you have 400 KTN tokens or more on your connected wallet',
  },
  {
    question:
      'I paid for the premium version, is it possible to transfer it to another wallet?',
    answear:
      'Unfortunately no, the premium version will only be available for the wallet from which it was paid for or which has 400 KTN tokens or more',
  },
  {
    question: 'Can I use the free version after the premium version expires?',
    answear: 'Of course, the free version is always available',
  },
  {
    question: 'What token can I use to pay for the premium version?',
    answear:
      'In the payment service, you can always familiarize yourself with the tokens available for payment',
  },
];

enum Action {
  Hold,
  Subscribe,
}

export enum SubscriptionMethod {
  email,
  code,
}

const SubscriptionBody = () => {
  const [action, setAction] = useState(Action.Subscribe);
  const authToken = useSelector(authTokenSelector);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onHold = () => {
    navigate(DEFAULT_URL_PATH);
  };

  const onSubscribe = async () => {
    dispatch(
      actionShowModal(ModalTypes.payment, {
        title: 'Payment',
        subtitle: 'Thank you for choosing our premium subscription!',
        description:
          'To complete your order, you will be redirected to the secure checkout page',
        icon: 'icon-gem',
        iconColor: 'var(--grey-600)',
        buttonText: (method: SubscriptionMethod) =>
          method === SubscriptionMethod.email
            ? 'Pay Now'
            : 'Redeem premium code',
        emailInput: true,
        onSubmit: async (value: string, method: SubscriptionMethod) => {
          if (!authToken) {
            return dispatch(
              actionCreateNotification({
                kind: 'warning',
                message: 'You need to be authroized for this action',
              })
            );
          }
          if (method === SubscriptionMethod.email) {
            const invoice = await UserAPI.fetchInvoice(
              value,
              enums.PricingPlan.premium,
              authToken,
              `${window.location.origin}/payment`
            );
            if (invoice?.status === 'ok' && invoice.url) {
              window.open(invoice.url);
            } else {
              dispatch(
                actionCreateNotification({
                  kind: 'warning',
                  message:
                    'Could not fetch payment link... Check if you are authorized or already subcribed to premium',
                })
              );
            }
          } else {
            const data = await UserAPI.checkPromoCode(value, authToken);
            if (data === null || data?.error || data?.status) {
              if (data?.error || data?.status) {
                dispatch(
                  actionCreateNotification({
                    kind: 'warning',
                    message:
                      'Could not procced your premium code... Check if you are authorized or already subcribed to premium',
                  })
                );
              }
              navigate('/promocode/fail');
            } else {
              navigate('/promocode/success');
            }
          }
        },
      })
    );
  };

  return (
    <div className='subscription_page'>
      <div className='subscription_page__container'>
        <div className='subscription_page__header'>
          <Link to='/' className='subscription_page__header__go_back'>
            <div className='subscription_page__header__go_back__icon icon-arrow-left' />
            <Text size={12}>Go to terminal</Text>
          </Link>
        </div>
        <div className='subscription_page__body'>
          <div className='subscription_page__body__packages'>
            <Text
              size={24}
              className='subscription_page__body__packages__title'
            >
              Unleash your trading power with Kattana Premium
            </Text>
            <div className='subscription_page__body__packages__description'>
              <Text size={16}>
                More tools and features, no limitation - pure trading experience
                with the most professional trading terminal for DEXs and CEXs
              </Text>
              {/* <Text size={16}>
                Simply hold 400 KTN on connected wallet or subscribe on Kattana
                Premium just right now!
              </Text> */}
            </div>
            {/* <div className='subscription_page__body__packages__actions'>
              <div
                className={`subscription_page__body__packages__actions__hold ${
                  action === Action.Hold
                    ? 'subscription_page__body__packages__actions__hold--active'
                    : ''
                } subscription_page__body__packages__actions__btn`}
                onClick={() => setAction(Action.Hold)}
              >
                <Text size={12}>Hold 400 KTN</Text>
              </div>
              <div className='subscription_page__body__packages__actions__divider' />
              <div
                className={`subscription_page__body__packages__actions__subscribe ${
                  action === Action.Subscribe
                    ? 'subscription_page__body__packages__actions__subscribe--active'
                    : ''
                } subscription_page__body__packages__actions__btn`}
                onClick={() => setAction(Action.Subscribe)}
              >
                <Text size={12}>Subscribe Now</Text>
              </div>
            </div> */}
          </div>
          <img
            src={kattana}
            className='subscription_page__body__default_banner'
            alt='Default Pricing Plan banner'
          />
          <div className='subscription_page__body__premium_banner'>
            <img src={kattanaPremium} alt='Premium Pricing Plan banner' />
            <Text size={20}>Premium</Text>
          </div>
          <div className='subscription_page__body__compare'>
            <div className='subscription_page__body__compare__features'>
              <FeatureTable
                rows={features}
                color='var(--grey-600)'
                weight={500}
              />
            </div>
            <div className='subscription_page__body__compare__common'>
              <div className='subscription_page__body__compare__common__plan'>
                <Text
                  size={16}
                  className='subscription_page__body__compare__common__plan__title'
                >
                  Kattana
                </Text>
                <Text
                  size={28}
                  className='subscription_page__body__compare__common__plan__price'
                >
                  <Text size={14}>$</Text>0
                </Text>
                <Text
                  size={14}
                  className='subscription_page__body__compare__common__plan__actions'
                >
                  Forever free
                </Text>
              </div>
              <FeatureTable
                rows={commons}
                color='var(--grey-600)'
                weight={400}
              />
            </div>
            <div className='subscription_page__body__compare__premium'>
              <Text
                size={10}
                className='subscription_page__body__compare__premium__meta'
              >
                Kattana with no limits
              </Text>
              <div className='subscription_page__body__compare__premium__plan'>
                <Text
                  size={16}
                  className='subscription_page__body__compare__premium__plan__title'
                >
                  Kattana Premium
                </Text>
                <div className='subscription_page__body__compare__premium__plan__price'>
                  <Text
                    size={28}
                    className='subscription_page__body__compare__premium__plan__price__value'
                  >
                    <Text size={14}>$</Text>
                    24.99&nbsp;
                    <Text size={14} className='text--grey-600'>
                      49.99&nbsp;&nbsp;
                      <Text size={14} style={{ style: { color: '#5d5fef' } }}>
                        -50%
                      </Text>
                    </Text>
                  </Text>
                  <Text
                    size={14}
                    className='subscription_page__body__compare__premium__plan__price__period'
                  >
                    / mo
                  </Text>
                  <Text
                    size={12}
                    className='subscription_page__body__compare__premium__plan__price__friday'
                  >
                    Holidays Sale
                  </Text>
                </div>
                <Button
                  action={action === Action.Hold ? onHold : onSubscribe}
                  text={action === Action.Hold ? 'Buy KTN' : 'Upgrade'}
                  variant='primary'
                  size='m'
                />
              </div>
              <FeatureTable rows={premiums} color='#FCDDEC' weight={400} />
            </div>
          </div>
          <div className='subscription_page__body__faq'>
            <Text size={24} className='subscription_page__body__faq__title'>
              Frequently asked questions, answered
            </Text>
            <FaqTable rows={faqs} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBody;
